// The browser half of crypto/MediaCipher.kt (docs/web-konzept.md, section 5.5): videos and files in
// segments, Tink's streaming AEAD "AES-GCM-HKDF" (AesGcmHkdfStreaming with HmacSha256, 32 byte
// derived keys, 1 MB ciphertext segments, no offset), with the MEDIA tag as associated data.
//
// Format, as Tink writes it:
//   header   = header length (1 byte, 40) || salt (32 bytes) || nonce prefix (7 bytes)
//   key      = HKDF-SHA256(ikm = MCK, salt, info = associated data), 32 bytes
//   segment i = AES-GCM(key, nonce prefix || i as 4 bytes big-endian || last ? 1 : 0, plaintext), no AAD
// The first segment shares its ciphertext segment size with the header. A plaintext that fills its
// segments exactly ends with its last full segment; only an empty one is a single empty segment.
// Checked against vectors from Tink itself (test/fixtures/tink-vectors.json) and back
// (crypto/src/test/.../WebVectorsTest.kt reads test/fixtures/web-vectors.json).

import { associatedData, ContentKind, KEY_BYTES } from "./contentCipher.ts";

export const SEGMENT_BYTES = 1 << 20;
const SALT_BYTES = KEY_BYTES;
const NONCE_PREFIX_BYTES = 7;
const HEADER_BYTES = 1 + SALT_BYTES + NONCE_PREFIX_BYTES;
const TAG_BYTES = 16;

type Bytes = Uint8Array<ArrayBuffer>;

/** The segment layout for one ciphertext segment size (tests use small ones; Voxie uses [SEGMENT_BYTES]). */
export class MediaLayout {
  readonly ciphertextSegmentBytes: number;

  constructor(ciphertextSegmentBytes: number = SEGMENT_BYTES) {
    this.ciphertextSegmentBytes = ciphertextSegmentBytes;
    if (ciphertextSegmentBytes <= HEADER_BYTES + TAG_BYTES) throw new Error("segment too small for header and tag");
  }

  get plaintextSegmentBytes(): number {
    return this.ciphertextSegmentBytes - TAG_BYTES;
  }

  get firstPlaintextSegmentBytes(): number {
    return this.ciphertextSegmentBytes - HEADER_BYTES - TAG_BYTES;
  }

  /** How many segments a plaintext of [plaintextBytes] has (at least one). */
  segments(plaintextBytes: number): number {
    if (plaintextBytes <= this.firstPlaintextSegmentBytes) return 1;
    return 1 + Math.ceil((plaintextBytes - this.firstPlaintextSegmentBytes) / this.plaintextSegmentBytes);
  }

  /** The ciphertext length for a plaintext of [plaintextBytes]; MediaCipher.ciphertextSize on the JVM. */
  ciphertextSize(plaintextBytes: number): number {
    return HEADER_BYTES + plaintextBytes + this.segments(plaintextBytes) * TAG_BYTES;
  }
}

async function deriveKey(mck: Bytes, salt: Bytes, use: "encrypt" | "decrypt"): Promise<CryptoKey> {
  if (mck.length !== KEY_BYTES) throw new Error("the MCK has 256 bits");
  const ikm = await crypto.subtle.importKey("raw", mck, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt, info: associatedData(ContentKind.MEDIA) },
    ikm,
    { name: "AES-GCM", length: KEY_BYTES * 8 },
    false,
    [use],
  );
}

function nonce(prefix: Bytes, segment: number, last: boolean): Bytes {
  const n = new Uint8Array(12);
  n.set(prefix);
  new DataView(n.buffer).setUint32(NONCE_PREFIX_BYTES, segment, false);
  n[11] = last ? 1 : 0;
  return n;
}

/** Encrypts a whole file at once (the browser holds it as a Blob anyway). */
export async function encryptMedia(plaintext: Bytes, mck: Bytes, layout = new MediaLayout()): Promise<Bytes> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const prefix = crypto.getRandomValues(new Uint8Array(NONCE_PREFIX_BYTES));
  const key = await deriveKey(mck, salt, "encrypt");
  const out = new Uint8Array(layout.ciphertextSize(plaintext.length));
  out[0] = HEADER_BYTES;
  out.set(salt, 1);
  out.set(prefix, 1 + SALT_BYTES);
  const count = layout.segments(plaintext.length);
  let read = 0;
  let written = HEADER_BYTES;
  for (let i = 0; i < count; i++) {
    const size = Math.min(i === 0 ? layout.firstPlaintextSegmentBytes : layout.plaintextSegmentBytes, plaintext.length - read);
    const sealed = await crypto.subtle.encrypt({ name: "AES-GCM", iv: nonce(prefix, i, i === count - 1), tagLength: TAG_BYTES * 8 }, key, plaintext.subarray(read, read + size));
    out.set(new Uint8Array(sealed), written);
    read += size;
    written += sealed.byteLength;
  }
  return out;
}

/** Decrypts a whole file; throws if anything was tampered with, cut off or appended. */
export async function decryptMedia(ciphertext: Bytes, mck: Bytes, layout = new MediaLayout()): Promise<Bytes> {
  if (ciphertext.length < HEADER_BYTES + TAG_BYTES || ciphertext[0] !== HEADER_BYTES) throw new Error("not a Voxie media blob");
  const salt = ciphertext.subarray(1, 1 + SALT_BYTES);
  const prefix = ciphertext.subarray(1 + SALT_BYTES, HEADER_BYTES);
  const key = await deriveKey(mck, salt, "decrypt");
  const body = ciphertext.length - HEADER_BYTES;
  const firstCipher = layout.ciphertextSegmentBytes - HEADER_BYTES;
  const count = body <= firstCipher ? 1 : 1 + Math.ceil((body - firstCipher) / layout.ciphertextSegmentBytes);
  const parts: Bytes[] = [];
  let total = 0;
  let at = HEADER_BYTES;
  for (let i = 0; i < count; i++) {
    const size = Math.min(i === 0 ? firstCipher : layout.ciphertextSegmentBytes, ciphertext.length - at);
    if (size < TAG_BYTES) throw new Error("truncated segment");
    const opened = new Uint8Array(
      await crypto.subtle.decrypt({ name: "AES-GCM", iv: nonce(prefix, i, i === count - 1), tagLength: TAG_BYTES * 8 }, key, ciphertext.subarray(at, at + size)),
    );
    parts.push(opened);
    total += opened.length;
    at += size;
  }
  const plaintext = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    plaintext.set(part, offset);
    offset += part.length;
  }
  return plaintext;
}
